<!-- Page header -->
<div class="np-page-header">
  <button pButton type="button" class="p-button-text p-button-sm" title="Back to batches" (click)="goBack()">
    <i class="pi pi-arrow-left"></i>
  </button>
  <span class="np-page-title">
    Batch {{ joPullBatchId }}
    <span class="np-badge ml-2" [ngClass]="canMarkComplete ? 'np-badge-in-process' : 'np-badge-complete'">
      {{ batchStatusLabel }}
    </span>
  </span>
</div>

<div *ngIf="isPageReady" class="p-3 flex flex-col gap-3 h-full">
  <!-- View toggle -->
  <div class="np-view-toggle" role="tablist">
    <button
      type="button"
      role="tab"
      [attr.aria-selected]="viewMode === 'product'"
      class="np-view-toggle-btn"
      [class.np-view-toggle-btn-active]="viewMode === 'product'"
      (click)="setViewMode('product')"
      title="Group by product (default pull view)"
    >
      <i class="pi pi-tag"></i>
      <span>By Product</span>
    </button>
    <button
      type="button"
      role="tab"
      [attr.aria-selected]="viewMode === 'jo'"
      class="np-view-toggle-btn"
      [class.np-view-toggle-btn-active]="viewMode === 'jo'"
      (click)="setViewMode('jo')"
      title="Group by job order"
    >
      <i class="pi pi-list"></i>
      <span>By Job Order</span>
    </button>
  </div>

  <!-- Action toolbar -->
  <div class="flex flex-wrap items-center gap-2">
    <button type="button" class="np-btn np-btn-secondary" (click)="onChangeBatch()">
      <i class="pi pi-refresh"></i>
      <span>Change Batch</span>
    </button>

    <button *ngIf="canMarkComplete" type="button" class="np-btn np-btn-success" [disabled]="isBusy()" (click)="onMarkBatchComplete()">
      <i class="pi pi-check"></i>
      <span>Mark Complete</span>
    </button>

    <button *ngIf="canUncomplete" type="button" class="np-btn np-btn-warning" [disabled]="isBusy()" (click)="onUncompleteBatch()">
      <i class="pi pi-undo"></i>
      <span>Reopen Batch</span>
    </button>

    <button
      type="button"
      class="np-btn np-btn-secondary"
      [disabled]="!canPrintItemDetailBarcodes()"
      (click)="onPrintBarcodes()"
    >
      <i class="pi pi-print"></i>
      <span>Print Barcodes</span>
    </button>

    <div class="flex items-center gap-2 ml-auto">
      <p-checkbox
        [(ngModel)]="shouldPrintBarcodes"
        (ngModelChange)="updateLocation()"
        name="shouldPrintBarcodes"
        inputId="shouldPrintBarcodes"
        [binary]="true"
      ></p-checkbox>
      <label for="shouldPrintBarcodes" class="text-sm">Auto-print barcodes</label>
    </div>
  </div>

  <!-- Search -->
  <span class="p-input-icon-left w-full">
    <i class="pi pi-search"></i>
    <input
      pInputText
      type="text"
      [(ngModel)]="searchText"
      placeholder="Search by manufacturer, SKU, description, features, JO #"
      class="w-full"
      title="Search lines"
    />
  </span>

  <!-- By Product view -->
  <div *ngIf="viewMode === 'product'" class="flex flex-col gap-2 overflow-y-auto flex-1">
    <div *ngFor="let group of filteredProductGroups" class="np-card flex flex-col gap-3" [attr.data-product-key]="group.key">
      <!-- Top row -->
      <div class="flex items-start justify-between gap-2">
        <div class="flex flex-col gap-0.5 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="font-semibold text-base">{{ group.manufacturerName }}</span>
            <span class="opacity-50">·</span>
            <span class="text-base">{{ group.style }}</span>
            <i
              *ngIf="group.inInventoryProgram"
              class="pi pi-info-circle text-blue-500"
              title="In account inventory program"
            ></i>
          </div>
          <div class="text-sm opacity-80">{{ group.description }}</div>
          <div class="text-xs opacity-70">{{ group.featureChoicesExtract }}</div>
        </div>

        <div class="flex flex-col items-end gap-1 shrink-0">
          <span
            *ngIf="group.needsBarcode"
            class="np-badge np-badge-await"
            title="One or more barcodes have not been printed in this group"
          >
            Barcode
          </span>
          <span class="text-xs opacity-60">
            {{ group.joDetails.length }} JO{{ group.joDetails.length !== 1 ? 's' : '' }}
          </span>
        </div>
      </div>

      <!-- Summed qty chips -->
      <div class="flex flex-wrap gap-2">
        <div class="np-qty-chip">
          <div class="np-qty-label">Order</div>
          <div class="np-qty-value">{{ group.orderQty }}</div>
        </div>
        <div class="np-qty-chip">
          <div class="np-qty-label">Cancel</div>
          <div class="np-qty-value">{{ group.cancelQty }}</div>
        </div>
        <div class="np-qty-chip">
          <div class="np-qty-label">Return</div>
          <div class="np-qty-value">{{ group.returnQty }}</div>
        </div>
        <div class="np-qty-chip">
          <div class="np-qty-label">Applied</div>
          <div class="np-qty-value">{{ group.appliedQty }}</div>
        </div>
        <div class="np-qty-chip">
          <div class="np-qty-label">PO</div>
          <div class="np-qty-value">{{ group.poRemainingQty }}</div>
        </div>
        <div class="np-qty-chip np-qty-emphasis">
          <div class="np-qty-label">Open</div>
          <div class="np-qty-value">{{ group.openQty }}</div>
        </div>
      </div>

      <!-- Pull button -->
      <div *ngIf="canDisplayPullProduct(group)" class="flex flex-col gap-2">
        <button type="button" class="np-btn np-btn-primary" (click)="togglePullProduct(group, $event)">
          <i class="pi pi-shopping-cart"></i>
          <span>{{ pullLabelProduct(group) }}</span>
          <i class="pi" [ngClass]="isPullProductExpanded(group) ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
        </button>

        <!-- Inline JoDetail picker -->
        <div *ngIf="isPullProductExpanded(group)" class="np-jod-picker flex flex-col gap-1">
          <div class="text-xs opacity-70 px-1">Which Job Order are you pulling for?</div>
          <button
            *ngFor="let jod of productGroupPullableJoDetails(group)"
            type="button"
            class="np-jod-pick"
            (click)="onPull(jod)"
          >
            <div class="flex flex-col items-start min-w-0">
              <span class="text-sm font-semibold">JO {{ jod.joHeaderId }}</span>
              <span class="text-xs opacity-70 truncate">{{ jod.joHeader?.account?.accountName }}</span>
            </div>
            <div class="flex items-center gap-3 ml-auto">
              <span class="text-xs opacity-70">Open</span>
              <span class="text-base font-bold">{{ jod.getUnresolvedQty() }}</span>
              <i class="pi pi-chevron-right opacity-50"></i>
            </div>
          </button>
        </div>
      </div>
    </div>

    <div *ngIf="filteredProductGroups.length === 0" class="text-center py-8 opacity-50">
      No products match
    </div>
  </div>

  <!-- By Job Order view -->
  <div *ngIf="viewMode === 'jo'" class="flex flex-col gap-4 overflow-y-auto flex-1">
    <div *ngFor="let group of filteredGroups" class="flex flex-col gap-2">
      <!-- JO group header -->
      <div class="np-jo-header" (click)="toggleGroup(group)">
        <i class="pi" [ngClass]="group.collapsed ? 'pi-chevron-right' : 'pi-chevron-down'"></i>
        <span class="font-semibold text-base">JO {{ group.joHeader.id }}</span>
        <span class="opacity-70 text-sm flex-1 truncate">{{ group.joHeader.account?.accountName }}</span>
        <span class="text-xs opacity-60">{{ group.joDetails.length }} line{{ group.joDetails.length !== 1 ? 's' : '' }}</span>
        <button
          *ngIf="canDeleteJoHeaderFromBatch(group.joHeader)"
          pButton
          type="button"
          class="p-button-text p-button-sm p-button-danger"
          title="Remove this JO from the batch"
          (click)="$event.stopPropagation(); onDeleteJoFromBatch(group.joHeader)"
        >
          <i class="pi pi-trash"></i>
        </button>
      </div>

      <!-- Line cards -->
      <div *ngIf="!group.collapsed" class="flex flex-col gap-2 pl-2">
        <div *ngFor="let jod of group.joDetails" class="np-card flex flex-col gap-3" [attr.data-jod-id]="jod.id">
          <!-- Top row: manufacturer · SKU · features + inventory icon -->
          <div class="flex items-start justify-between gap-2">
            <div class="flex flex-col gap-0.5 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-semibold text-base">{{ jod.product?.productType?.manufacturer?.name }}</span>
                <span class="opacity-50">·</span>
                <span class="text-base">{{ jod.product?.productType?.style }}</span>
                <i
                  *ngIf="isInInventoryProgram(jod)"
                  class="pi pi-info-circle text-blue-500"
                  title="In account inventory program"
                ></i>
              </div>
              <div class="text-sm opacity-80">{{ jod.product?.productType?.description }}</div>
              <div class="text-xs opacity-70">{{ jod.product?.featureChoicesExtract }}</div>
            </div>

            <div class="flex flex-col items-end gap-1 shrink-0">
              <span
                *ngIf="needsBarcode(jod)"
                class="np-badge np-badge-await"
                title="One or more barcodes have not been printed"
              >
                Barcode
              </span>
              <button
                *ngIf="addonCount(jod) > 0"
                type="button"
                class="np-addon-toggle"
                (click)="toggleAddons(jod, $event)"
                [title]="isAddonExpanded(jod) ? 'Hide addons' : 'Show addons'"
              >
                <i class="pi np-addon-toggle-icon" [ngClass]="isAddonExpanded(jod) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
                +{{ addonCount(jod) }} addon{{ addonCount(jod) !== 1 ? 's' : '' }}
              </button>
            </div>
          </div>

          <!-- Inline addon details -->
          <div *ngIf="isAddonExpanded(jod) && addonCount(jod) > 0" class="np-addon-details flex flex-col gap-1 pl-3">
            <div *ngFor="let jda of jod.joDetailAddons" class="text-xs flex items-baseline gap-2">
              <span class="font-semibold">{{ jda.addon?.nameAndLocation }}</span>
              <span *ngIf="jda.additionalInfo" class="opacity-80">{{ jda.additionalInfo }}</span>
              <span *ngIf="jda.unitPriceAmt" class="opacity-60 ml-auto">{{ jda.unitPriceAmt | currency }}</span>
            </div>
          </div>

          <!-- Qty chips -->
          <div class="flex flex-wrap gap-2">
            <div class="np-qty-chip">
              <div class="np-qty-label">Order</div>
              <div class="np-qty-value">{{ jod.orderQty }}</div>
            </div>
            <div class="np-qty-chip">
              <div class="np-qty-label">Cancel</div>
              <div class="np-qty-value">{{ jod.cancelQty }}</div>
            </div>
            <div class="np-qty-chip">
              <div class="np-qty-label">Return</div>
              <div class="np-qty-value">{{ jod.returnQty }}</div>
            </div>
            <div class="np-qty-chip">
              <div class="np-qty-label">Applied</div>
              <div class="np-qty-value">{{ jod.getItemDetailsRcvdQty() }}</div>
            </div>
            <div class="np-qty-chip">
              <div class="np-qty-label">PO</div>
              <div class="np-qty-value">{{ jod.getPoDetailsRemainingQty() }}</div>
            </div>
            <div class="np-qty-chip np-qty-emphasis">
              <div class="np-qty-label">Open</div>
              <div class="np-qty-value">{{ jod.getUnresolvedQty() }}</div>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="flex gap-2 flex-wrap">
            <button
              *ngIf="canDisplayPull(jod)"
              type="button"
              class="np-btn np-btn-primary"
              (click)="onPull(jod)"
            >
              <i class="pi pi-shopping-cart"></i>
              <span>{{ pullLabel(jod) }}</span>
            </button>
            <button
              *ngIf="canDisplayUndo(jod)"
              type="button"
              class="np-btn np-btn-secondary"
              (click)="onUndo(jod)"
            >
              <i class="pi pi-undo"></i>
              <span>Undo</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="filteredGroups.length === 0" class="text-center py-8 opacity-50">
      No lines match
    </div>
  </div>
</div>
